import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { Layout } from '../components/Layout';

interface User {
  id: number;
  name: string | null;
  email: string | null;
  phone: string | null;
  kakao_id: string;
}

interface Coupon {
  id: number;
  code: string;
  name: string;
  description: string | null;
  coupon_type: string;
  discount_type: string;
  discount_value: number;
  max_discount_amount: number | null;
  min_order_amount: number | null;
  is_active: boolean;
}

interface UserCoupon {
  id: number;
  user: User;
  coupon: Coupon;
  issued_at: string;
  valid_from: string;
  valid_until: string;
  is_used: boolean;
  used_at: string | null;
  reservation_id: number | null;
  issue_source: string | null;
  created_at: string;
  updated_at: string;
}

interface UserCouponsResponse {
  user_coupons: UserCoupon[];
  total: number;
  page: number;
  limit: number;
}

interface Statistics {
  total_issued: number;
  total_used: number;
  total_available: number;
  total_expired: number;
  usage_rate: number;
}

export const UserCouponsPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [isUsed, setIsUsed] = useState<boolean | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const queryClient = useQueryClient();

  const { data: userCouponsData, isLoading, error } = useQuery<UserCouponsResponse>({
    queryKey: ['user-coupons', page, limit, search, isUsed],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('skip', ((page - 1) * limit).toString());
      params.append('limit', limit.toString());
      if (search) params.append('search', search);
      if (isUsed !== null) params.append('is_used', isUsed.toString());

      const response = await apiClient.get<UserCouponsResponse>(`/user-coupons?${params.toString()}`);
      return response.data;
    },
  });

  const { data: statistics } = useQuery<Statistics>({
    queryKey: ['user-coupons-stats'],
    queryFn: async () => {
      const response = await apiClient.get<Statistics>('/user-coupons/statistics/summary');
      return response.data;
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/user-coupons/${id}`);
    },
    onSuccess: () => {
      alert('쿠폰이 회수되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['user-coupons'] });
      queryClient.invalidateQueries({ queryKey: ['user-coupons-stats'] });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || '쿠폰 회수에 실패했습니다.';
      alert(errorMessage);
    },
  });

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleReset = () => {
    setSearchInput('');
    setSearch('');
    setIsUsed(null);
    setPage(1);
  };

  const handleRevoke = (id: number) => {
    if (window.confirm('정말로 이 쿠폰을 회수하시겠습니까?')) {
      revokeMutation.mutate(id);
    }
  };

  const userCoupons = userCouponsData?.user_coupons || [];
  const total = userCouponsData?.total || 0;

  const couponTypeLabels: Record<string, string> = {
    payment_discount: '결제할인',
    delivery_discount: '배송비할인',
    car_seat_free: '카시트무료',
    insurance_discount: '보험할인',
    other: '기타',
  };

  const getIssueSourceLabel = (source: string | null) => {
    if (!source) return '-';
    if (source.startsWith('promotion_signup')) return '회원가입';
    if (source.startsWith('promotion_login')) return '로그인';
    if (source.startsWith('manual_admin')) return '관리자발급';
    return source;
  };

  const isExpired = (validUntil: string) => {
    return new Date(validUntil) < new Date();
  };

  return (
    <Layout>
      <div className="px-2 py-4">
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-2xl font-semibold text-gray-900">회원쿠폰 관리</h1>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-5 gap-3 mb-4">
            <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 hover:shadow-md transition-shadow">
              <div className="text-xs text-slate-500 mb-1">전체 발급</div>
              <div className="text-xl font-bold text-slate-900">{statistics.total_issued.toLocaleString()}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 hover:shadow-md transition-shadow">
              <div className="text-xs text-slate-500 mb-1">사용 완료</div>
              <div className="text-xl font-bold text-emerald-600">{statistics.total_used.toLocaleString()}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 hover:shadow-md transition-shadow">
              <div className="text-xs text-slate-500 mb-1">사용 가능</div>
              <div className="text-xl font-bold text-sky-600">{statistics.total_available.toLocaleString()}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 hover:shadow-md transition-shadow">
              <div className="text-xs text-slate-500 mb-1">만료됨</div>
              <div className="text-xl font-bold text-rose-600">{statistics.total_expired.toLocaleString()}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 hover:shadow-md transition-shadow">
              <div className="text-xs text-slate-500 mb-1">사용률</div>
              <div className="text-xl font-bold text-violet-600">{statistics.usage_rate}%</div>
            </div>
          </div>
        )}

        {/* Search Form - More Compact */}
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-3 mb-4">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                검색어
              </label>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="block w-full px-2.5 py-1.5 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                placeholder="이름, 이메일, 전화번호"
              />
            </div>
            <div className="w-32">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                사용 여부
              </label>
              <select
                value={isUsed === null ? '' : isUsed.toString()}
                onChange={(e) => {
                  setIsUsed(e.target.value === '' ? null : e.target.value === 'true');
                  setPage(1);
                }}
                className="block w-full px-2.5 py-1.5 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
              >
                <option value="">전체</option>
                <option value="false">미사용</option>
                <option value="true">사용완료</option>
              </select>
            </div>
            <button
              onClick={handleSearch}
              className="w-16 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700"
            >
              조회
            </button>
            <button
              onClick={handleReset}
              className="w-16 py-1.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50"
            >
              초기화
            </button>
          </div>
        </div>

        {/* Results - Compact List */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">
              회원쿠폰 목록
              {total > 0 && <span className="ml-2 text-sm font-normal text-gray-600">({total.toLocaleString()}건)</span>}
            </h2>
          </div>

          {isLoading ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
              <p className="mt-3 text-slate-600 text-sm">로딩 중...</p>
            </div>
          ) : error ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <p className="text-rose-600 text-sm">회원쿠폰 조회에 실패했습니다.</p>
            </div>
          ) : userCoupons.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <p className="text-slate-500 text-sm">회원쿠폰이 없습니다.</p>
            </div>
          ) : (
            <>
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-700">회원</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-700">쿠폰명</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-700">종류</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-700">할인금액</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-700">발급일</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-700">만료일</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-700">발급출처</th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-700">상태</th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-700">관리</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {userCoupons.map((uc) => (
                      <tr key={uc.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <div className="text-xs font-semibold text-gray-900">
                            {uc.user.name || uc.user.kakao_id}
                          </div>
                          <div className="text-xs text-gray-600">{uc.user.phone}</div>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="text-xs font-semibold text-gray-900 max-w-xs truncate">
                            {uc.coupon.name}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                            {couponTypeLabels[uc.coupon.coupon_type]}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-semibold">
                            {uc.coupon.discount_type === 'fixed'
                              ? `${Math.floor(uc.coupon.discount_value).toLocaleString()}원`
                              : `${uc.coupon.discount_value}%`}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-xs text-gray-600">
                          {new Date(uc.issued_at).toLocaleDateString('ko-KR')}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-xs text-gray-600">
                          {new Date(uc.valid_until).toLocaleDateString('ko-KR')}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-xs text-gray-600">
                          {getIssueSourceLabel(uc.issue_source)}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-center">
                          {uc.is_used ? (
                            <span className="px-2.5 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                              사용완료
                            </span>
                          ) : isExpired(uc.valid_until) ? (
                            <span className="px-2.5 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                              만료
                            </span>
                          ) : (
                            <span className="px-2.5 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                              사용가능
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-center">
                          {!uc.is_used && (
                            <button
                              onClick={() => handleRevoke(uc.id)}
                              className="px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors"
                            >
                              회수
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600">
                    페이지 <span className="font-medium text-gray-900">{page}</span> / {Math.ceil(total / limit)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    이전
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={userCoupons.length < limit}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    다음
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};
